import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { fmtMachineLabel, sortMachinesByIdentifier } from '../utils/machineLabel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  machineQualitySettingsService,
  machineService,
  defectReasonService,
} from '../services/api';

const DEFAULT_PERIOD = 240;

function QualitySettingsPage() {
  const [settings, setSettings] = useState([]);
  const [machines, setMachines] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    machine: '',
    period_minutes: DEFAULT_PERIOD,
    schedule_from_shift: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonEditing, setReasonEditing] = useState(null);
  const [reasonForm, setReasonForm] = useState({
    code: '',
    name: '',
    is_active: true,
    comment_required: false,
    sort_order: 0,
  });
  const [reasonSaving, setReasonSaving] = useState(false);
  const [reasonError, setReasonError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsRes, machinesRes, reasonsRes] = await Promise.all([
        machineQualitySettingsService.getAll(),
        machineService.getAllForSelect ? machineService.getAllForSelect() : machineService.getAll(),
        defectReasonService.getAllAdmin(),
      ]);
      setSettings(settingsRes.data.results || settingsRes.data);
      setMachines(sortMachinesByIdentifier(machinesRes.data.results || machinesRes.data || []));
      setReasons(reasonsRes.data.results || reasonsRes.data || []);
    } catch (err) {
      console.error('Помилка завантаження:', err);
    } finally {
      setLoading(false);
    }
  };

  const machinesWithoutSettings = machines.filter(
    (m) => !settings.some((s) => s.machine === m.id || s.machine_id === m.id)
  );

  const handleOpen = (item = null) => {
    setError('');
    if (item) {
      setEditing(item.id);
      setFormData({
        machine: item.machine || item.machine_id,
        period_minutes: item.period_minutes ?? DEFAULT_PERIOD,
        schedule_from_shift: item.schedule_from_shift !== false,
      });
    } else {
      setEditing(null);
      setFormData({
        machine: machinesWithoutSettings[0]?.id || '',
        period_minutes: DEFAULT_PERIOD,
        schedule_from_shift: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    const period = parseInt(formData.period_minutes, 10);
    if (isNaN(period) || period < 1) {
      setError('Період має бути цілим числом хвилин (мін. 1)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        machine: formData.machine,
        period_minutes: period,
        schedule_from_shift: !!formData.schedule_from_shift,
      };
      if (editing) {
        await machineQualitySettingsService.upsert({ id: editing, ...payload });
      } else {
        if (!formData.machine) {
          setError('Оберіть станок');
          setSaving(false);
          return;
        }
        await machineQualitySettingsService.upsert(payload);
      }
      await loadData();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити налаштування для цього станка?')) return;
    try {
      await machineQualitySettingsService.delete(id);
      await loadData();
    } catch (err) {
      console.error('Помилка видалення:', err);
      alert(err.response?.data?.detail || 'Не вдалося видалити');
    }
  };

  const openReason = (item = null) => {
    setReasonError('');
    if (item) {
      setReasonEditing(item.id);
      setReasonForm({
        code: item.code || '',
        name: item.name || '',
        is_active: item.is_active !== false,
        comment_required: !!item.comment_required,
        sort_order: item.sort_order ?? 0,
      });
    } else {
      setReasonEditing(null);
      setReasonForm({
        code: '',
        name: '',
        is_active: true,
        comment_required: false,
        sort_order: (reasons.length + 1) * 10,
      });
    }
    setReasonOpen(true);
  };

  const saveReason = async () => {
    setReasonError('');
    const code = (reasonForm.code || '').trim().toUpperCase();
    const name = (reasonForm.name || '').trim();
    if (!code || !name) {
      setReasonError('Код і назва обовʼязкові');
      return;
    }
    setReasonSaving(true);
    try {
      const payload = {
        code,
        name,
        is_active: !!reasonForm.is_active,
        comment_required: !!reasonForm.comment_required,
        sort_order: parseInt(reasonForm.sort_order, 10) || 0,
      };
      if (reasonEditing) {
        await defectReasonService.update(reasonEditing, payload);
      } else {
        await defectReasonService.create(payload);
      }
      setReasonOpen(false);
      await loadData();
    } catch (err) {
      const d = err.response?.data;
      setReasonError(d?.code?.[0] || d?.detail || 'Помилка збереження');
    } finally {
      setReasonSaving(false);
    }
  };

  const toggleReasonActive = async (item) => {
    try {
      await defectReasonService.update(item.id, { is_active: !item.is_active });
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Не вдалося змінити');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">ВКЯ — налаштування перевірок</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          disabled={machinesWithoutSettings.length === 0}
        >
          Додати налаштування
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Планові перевірки: на початку зміни, далі кожні N хвилин від початку зміни
        (напр. 240 хв → 08:00 → 12:00 → 16:00). При запуску замовлення на ТПА — окрема перевірка.
        Нагадування йдуть ролі ВКЯ.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Станок</TableCell>
              <TableCell>Період (хв)</TableCell>
              <TableCell>Розклад</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="textSecondary">
                    Немає налаштувань. Без запису для ТПА діє дефолт: 240 хв від початку зміни.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              settings.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.machine_name || item.machine_identifier
                      ? fmtMachineLabel({
                          name: item.machine_name,
                          identifier: item.machine_identifier,
                        })
                      : `Станок #${item.machine}`}
                  </TableCell>
                  <TableCell>{item.period_minutes}</TableCell>
                  <TableCell>
                    {item.schedule_from_shift !== false
                      ? 'Від початку зміни'
                      : 'Від останньої перевірки'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 4 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Довідник причин браку</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openReason()}>
          Додати причину
        </Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        У формі ВКЯ показуються код і назва. Вимкнену причину в нових перевірках обрати не можна.
      </Alert>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Код</TableCell>
              <TableCell>Причина</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Коментар обовʼязковий</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reasons.map((r) => (
              <TableRow key={r.id} sx={{ opacity: r.is_active ? 1 : 0.55 }}>
                <TableCell><b>{r.code}</b></TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.is_active ? 'Активна' : 'Вимкнена'}
                    color={r.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{r.comment_required ? 'Так' : '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openReason(r)}>
                    <EditIcon />
                  </IconButton>
                  <Button size="small" onClick={() => toggleReasonActive(r)} sx={{ textTransform: 'none' }}>
                    {r.is_active ? 'Вимкнути' : 'Увімкнути'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати налаштування' : 'Додати налаштування ВКЯ'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Typography color="error">{error}</Typography>}
            <FormControl fullWidth disabled={!!editing}>
              <InputLabel id="machine-label">Станок</InputLabel>
              <Select
                labelId="machine-label"
                value={formData.machine}
                label="Станок"
                onChange={(e) => setFormData({ ...formData, machine: e.target.value })}
              >
                {(editing ? machines : machinesWithoutSettings).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {fmtMachineLabel(m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Період (хвилин)"
              type="number"
              inputProps={{ min: 1 }}
              value={formData.period_minutes}
              onChange={(e) =>
                setFormData({ ...formData, period_minutes: e.target.value })
              }
              helperText="240 = кожні 4 години від початку зміни"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.schedule_from_shift}
                  onChange={(e) =>
                    setFormData({ ...formData, schedule_from_shift: e.target.checked })
                  }
                />
              }
              label="Розклад від початку зміни (рекомендовано)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reasonOpen} onClose={() => setReasonOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{reasonEditing ? 'Редагувати причину' : 'Нова причина браку'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {reasonError && <Typography color="error">{reasonError}</Typography>}
            <TextField
              label="Код"
              value={reasonForm.code}
              onChange={(e) => setReasonForm({ ...reasonForm, code: e.target.value })}
              disabled={!!reasonEditing}
              helperText="Напр. ПР, НВ, ІН"
              fullWidth
            />
            <TextField
              label="Назва"
              value={reasonForm.name}
              onChange={(e) => setReasonForm({ ...reasonForm, name: e.target.value })}
              fullWidth
              multiline
            />
            <TextField
              label="Порядок"
              type="number"
              value={reasonForm.sort_order}
              onChange={(e) => setReasonForm({ ...reasonForm, sort_order: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!reasonForm.is_active}
                  onChange={(e) => setReasonForm({ ...reasonForm, is_active: e.target.checked })}
                />
              }
              label="Активна"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!reasonForm.comment_required}
                  onChange={(e) => setReasonForm({ ...reasonForm, comment_required: e.target.checked })}
                />
              }
              label="Коментар обовʼязковий"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={saveReason} disabled={reasonSaving}>
            {reasonSaving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QualitySettingsPage;
